import { palette } from '../../styles/tokens';

// ── Tabs ──
export type ProjectTab = 'active' | 'completed';
export const PROJECT_TABS: { key: ProjectTab; label: string }[] = [
  { key: 'active', label: 'Active Projects' },
  { key: 'completed', label: 'Completed' },
];

// ── Project statuses ──
export const PROJECT_STATUSES: Record<string, { label: string; bg: string; fg: string }> = {
  lead:               { label: 'Lead',               bg: palette.g100,       fg: palette.g500 },
  quote_drafting:     { label: 'Quote Drafting',     bg: palette.g100,       fg: palette.g500 },
  quote_sent:         { label: 'Quote Sent',         bg: palette.blueWash,   fg: palette.blue },
  quoted:             { label: 'Quoted',             bg: palette.blueWash,   fg: palette.blue },
  approved:           { label: 'Approved',           bg: palette.greenWash,  fg: palette.green },
  declined:           { label: 'Declined',           bg: palette.redWash,    fg: palette.red },
  deposit_pending:    { label: 'Deposit Pending',    bg: palette.amberWash,  fg: palette.amber },
  in_progress:        { label: 'In Progress',        bg: palette.accentWash, fg: palette.accentDeep },
  on_hold:            { label: 'On Hold',            bg: palette.amberWash,  fg: palette.amber },
  work_complete:      { label: 'Work Complete',      bg: palette.greenWash,  fg: palette.green },
  complete:           { label: 'Complete',           bg: palette.greenWash,  fg: palette.green },
  final_invoice_sent: { label: 'Final Invoice Sent', bg: '#EDE9FE',         fg: '#7C3AED' },
  invoiced:           { label: 'Invoiced',           bg: '#EDE9FE',         fg: '#7C3AED' },
  paid:               { label: 'Paid',               bg: palette.greenWash,  fg: palette.green },
  closed:             { label: 'Closed',             bg: palette.g100,       fg: palette.g500 },
  cancelled:          { label: 'Cancelled',          bg: palette.redWash,    fg: palette.red },
};

export const ACTIVE_STATUSES = ['lead', 'quote_drafting', 'quote_sent', 'quoted', 'approved', 'declined', 'deposit_pending', 'in_progress', 'on_hold', 'invoiced', 'final_invoice_sent'];
export const COMPLETED_STATUSES = ['work_complete', 'complete', 'paid', 'closed', 'cancelled'];
export const STATUS_FLOW = ['lead', 'quote_drafting', 'quote_sent', 'quoted', 'approved', 'deposit_pending', 'in_progress', 'work_complete', 'complete', 'final_invoice_sent', 'invoiced', 'paid', 'closed'];

// ── Phase statuses ──
export const PHASE_STATUSES: Record<string, { label: string; bg: string; fg: string }> = {
  not_started: { label: 'Not Started', bg: palette.g100,      fg: palette.g500 },
  in_progress: { label: 'In Progress', bg: palette.accentWash, fg: palette.accentDeep },
  completed:   { label: 'Completed',   bg: palette.greenWash, fg: palette.green },
  skipped:     { label: 'Skipped',     bg: palette.g100,      fg: palette.g400 },
};

// ── Task statuses ──
export const TASK_STATUSES: Record<string, { label: string; bg: string; fg: string }> = {
  not_started: { label: 'Not Started', bg: palette.g100,      fg: palette.g500 },
  in_progress: { label: 'In Progress', bg: palette.accentWash, fg: palette.accentDeep },
  completed:   { label: 'Completed',   bg: palette.greenWash, fg: palette.green },
};
export const TASK_STATUS_FLOW = ['not_started', 'in_progress', 'completed'];

// ── Gate types ──
export const GATE_TYPES: Record<string, { label: string; bg: string; fg: string }> = {
  inspection:        { label: 'Inspection',        bg: palette.amberWash, fg: palette.amber },
  client_approval:   { label: 'Client Approval',   bg: palette.blueWash,  fg: palette.blue },
  permit:            { label: 'Permit',             bg: '#EDE9FE',        fg: '#7C3AED' },
  deposit:           { label: 'Deposit',            bg: palette.greenWash, fg: palette.green },
  material_delivery: { label: 'Material Delivery',  bg: '#FFF7ED',        fg: '#CA5010' },
  custom:            { label: 'Custom',             bg: palette.g100,      fg: palette.g600 },
};

export const GATE_STATUSES: Record<string, { label: string; bg: string; fg: string }> = {
  pending:  { label: 'Pending',  bg: palette.amberWash, fg: palette.amber },
  resolved: { label: 'Resolved', bg: palette.greenWash, fg: palette.green },
  waived:   { label: 'Waived',   bg: palette.g100,      fg: palette.g500 },
  blocking: { label: 'Blocking', bg: palette.redWash,   fg: palette.red },
};

// ── Quote statuses ──
export const QUOTE_STATUSES: Record<string, { label: string; bg: string; fg: string }> = {
  draft:    { label: 'Draft',    bg: palette.g100,      fg: palette.g500 },
  sent:     { label: 'Sent',     bg: palette.blueWash,  fg: palette.blue },
  approved: { label: 'Approved', bg: palette.greenWash, fg: palette.green },
  declined: { label: 'Declined', bg: palette.redWash,   fg: palette.red },
};

// ── Line item types ──
export const LINE_TYPES: Record<string, string> = {
  labour: 'Labour', materials: 'Materials', subcontractor: 'Subcontractor',
  permit_fee: 'Permit Fee', allowance: 'Allowance', discount: 'Discount',
};

// ── Source types ──
export const SOURCE_TYPES: Record<string, string> = {
  new_client: 'New Client', referral: 'Referral', repeat: 'Repeat',
  online: 'Online', marketing: 'Marketing', walk_in: 'Walk-In', other: 'Other',
};

// ── Photo tags ──
export const PHOTO_TAGS: Record<string, { label: string; bg: string; fg: string }> = {
  before: { label: 'Before', bg: palette.amberWash, fg: palette.amber },
  during: { label: 'During', bg: palette.blueWash,  fg: palette.blue },
  after:  { label: 'After',  bg: palette.greenWash, fg: palette.green },
};

// ── Expense categories ──
export const EXPENSE_CATEGORIES = [
  'materials', 'equipment', 'subcontractor', 'permits', 'tools',
  'travel', 'labour', 'disposal', 'other',
];

// ── Payment types ──
export const PAYMENT_TYPES: Record<string, string> = {
  deposit: 'Deposit', progress: 'Progress', final: 'Final',
};

// ── Table styles (reused from Jobs pattern) ──
export const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap',
};
export const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};
export const LABEL: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block',
};
export const INPUT: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800,
  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const,
};
export const SECTION_HEADER: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: palette.g700, marginBottom: 10,
};
